import { Combobox, Group, PillsInput, Pill, Input, CheckIcon, useCombobox } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import type { RouterOutputs } from '~/trpc-extra/api';

export function MultiSelectMaxDisplayedItems({
  testSuites = [],
  value,
  disabled,
  onChange,
}: {
  testSuites?: RouterOutputs['testSuite']['listWithLatestRun'];
  value: { id: string; name: string }[];
  disabled?: boolean;
  onChange: (value: { id: string; name: string }[]) => void;
}) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  });

  const handleValueSelect = (testSuiteName: string) => {
    const testSuite = testSuites.find((x) => x.name === testSuiteName);
    if (!testSuite) {
      return;
    }

    const newValue =
      value.some((v) => v.id === testSuite.id) ?
        value.filter((v) => v.id !== testSuite.id)
      : [...value, { id: testSuite.id, name: testSuite.name }];

    onChange(newValue);
  };

  const handleValueRemove = (testSuiteToRemove: { id: string; name: string }) =>
    onChange(value.filter((v) => v.id !== testSuiteToRemove.id));

  const values = value.slice(0, 1).map((item) => <Pill key={item.id}>{item.name}</Pill>);

  const options = testSuites.map((testSuite) => (
    <Combobox.Option
      value={testSuite.name}
      key={testSuite.id}
      active={value.some((v) => v.id === testSuite.id)}
      disabled={disabled}
    >
      <Group
        justify="space-between"
        wrap="nowrap"
      >
        <span>{testSuite.name}</span>
        {value.some((v) => v.id === testSuite.id) && <CheckIcon size={12} />}
      </Group>
    </Combobox.Option>
  ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleValueSelect}
      withinPortal={false}
      transitionProps={{ transition: 'pop', duration: 100 }}
    >
      <Combobox.DropdownTarget>
        <PillsInput
          pointer
          onClick={() => combobox.toggleDropdown()}
        >
          <Pill.Group>
            {value.length > 0 ?
              <>
                {values}
                {value.length > 1 && <Pill>+{value.length - 1} more</Pill>}
              </>
            : <>
                <IconPlus size={14} />
                <Input.Placeholder>Add run groups</Input.Placeholder>
              </>
            }

            <Combobox.EventsTarget>
              <PillsInput.Field
                type="hidden"
                onBlur={() => combobox.closeDropdown()}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace') {
                    event.preventDefault();
                    if (value.length > 0) {
                      handleValueRemove(value.at(-1)!);
                    }
                  }
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
